import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Content from '../../components/Content';
import Loader from '../../components/Loader';
import Error from '../../components/Error';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';
import styles from './SwitchCAM.module.css';
import Modal from '../../components/Modal';
import ModalHeading from '../../components/Modal/ModalHeading';

const FRONTEND_IMPORT_SWITCH_CAM_ERROR_CODES = {
    INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
    SINGLE_ENTRY_FAILED: ErrorCodes.API_INTERNAL_ERROR,
    FILE_IMPORT_FAILED: ErrorCodes.FILE_PROCESSING_FAILED,
    VALIDATION_FAILED: ErrorCodes.API_INVALID_PARAMETER,
};

const buildImportSwitchCamSupportError = (errorCode, pageInfo, sourceMessage) =>
    formatFrontendSupportError({
        errorCode,
        pageInfo,
        sourceMessage,
    });

const ImportSwitchCAM = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [success, setSuccess] = useState({ single: false, file: false });
    const [showModal, setShowModal] = useState(false);
    const [userCompany, setUserCompany] = useState('');
    const [availableCompanies, setAvailableCompanies] = useState([]);
    const [targetCompany, setTargetCompany] = useState('');
    const [supportErrorMessage, setSupportErrorMessage] = useState('');
    const [copiedSupportError, setCopiedSupportError] = useState(false);

    // Single entry state
    const [singleEntry, setSingleEntry] = useState({
        company: '',
        switch_name: '',
        mac_address: '',
        vlan_id: '',
        interface: '',
        entry_type: 'Dynamic',
        age: '',
        error: ''
    });

    // File upload state
    const [camFile, setCamFile] = useState({ value: null, error: '' });

    const setImportSwitchCamSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
        setSupportErrorMessage(buildImportSwitchCamSupportError(errorCode, pageInfo, sourceMessage));
        setCopiedSupportError(false);
    }, []);

    const clearImportSwitchCamSupportError = React.useCallback((force = false) => {
        if (!force) return;
        setSupportErrorMessage('');
        setCopiedSupportError(false);
    }, []);

    const handleCopySupportError = React.useCallback(async () => {
        if (!supportErrorMessage) return;
        try {
            await navigator.clipboard.writeText(supportErrorMessage);
            setCopiedSupportError(true);
            setTimeout(() => setCopiedSupportError(false), 2000);
        } catch (copyError) {
            setCopiedSupportError(false);
        }
    }, [supportErrorMessage]);

    // Fetch user session and available companies
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch user session and available companies
                const response = await axios.get('/api/upload-cam-switches');
                if (response.data && response.data.status) {
                    const currentUserCompany = response.data.user_company || 'None';
                    const availableTargets = response.data.available_upload_targets || [];
                    
                    setUserCompany(currentUserCompany);
                    setAvailableCompanies(availableTargets);
                    
                    if (availableTargets.length > 0) {
                        setTargetCompany(availableTargets[0]);
                        setSingleEntry(prev => ({ ...prev, company: availableTargets[0] }));
                    }
                    clearImportSwitchCamSupportError(true);
                } else {
                    setError(true);
                    setImportSwitchCamSupportError(
                        FRONTEND_IMPORT_SWITCH_CAM_ERROR_CODES.INITIAL_LOAD_FAILED,
                        'Import Switch CAM Page / Initial Data Load Failed',
                        response?.data?.msg || 'Initial upload-cam-switches request did not return success'
                    );
                }
            } catch (err) {
                console.error('Error fetching initial data:', err);
                setError(true);
                setImportSwitchCamSupportError(
                    FRONTEND_IMPORT_SWITCH_CAM_ERROR_CODES.INITIAL_LOAD_FAILED,
                    'Import Switch CAM Page / Initial Data Request Error',
                    err?.response?.data?.msg || err?.message || String(err)
                );
            }
        };
        
        fetchData();
    }, [clearImportSwitchCamSupportError, setImportSwitchCamSupportError]);

    const openFileModal = () => {
        setCamFile({ value: null, error: '' });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setCamFile({ value: null, error: '' });
        setSuccess({ ...success, file: false });
    };

    const handleAddSingleEntry = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        if (!singleEntry.switch_name.trim() || !singleEntry.mac_address.trim() || 
            !singleEntry.vlan_id.trim() || !singleEntry.interface.trim()) {
            setSingleEntry({ 
                ...singleEntry, 
                error: 'Switch name, MAC address, VLAN ID, and interface are required' 
            });
            setImportSwitchCamSupportError(
                FRONTEND_IMPORT_SWITCH_CAM_ERROR_CODES.VALIDATION_FAILED,
                'Import Switch CAM Page / Single Entry Validation Failed',
                'Required CAM entry fields are missing'
            );
            return;
        }

        // Validate MAC address format
        const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
        if (!macRegex.test(singleEntry.mac_address)) {
            setSingleEntry({ 
                ...singleEntry, 
                error: 'Invalid MAC address format. Use format: XX:XX:XX:XX:XX:XX or XX-XX-XX-XX-XX-XX' 
            });
            setImportSwitchCamSupportError(
                FRONTEND_IMPORT_SWITCH_CAM_ERROR_CODES.VALIDATION_FAILED,
                'Import Switch CAM Page / MAC Address Validation Failed',
                'Invalid MAC address format for single CAM entry'
            );
            return;
        }

        setLoading(true);
        
        try {
            // Use the selected company for the CSV data
            const companyToUse = singleEntry.company || targetCompany;
            const csvData = `Company,switch_name,mac_address,vlan_id,interface,entry_type,age\n${companyToUse},${singleEntry.switch_name},${singleEntry.mac_address},${singleEntry.vlan_id},${singleEntry.interface},${singleEntry.entry_type},${singleEntry.age || ''}`;
            
            const formData = new FormData();
            const csvBlob = new Blob([csvData], { type: 'text/csv' });
            formData.append('camFile', csvBlob, 'single_entry.csv');
            
            if (companyToUse) {
                formData.append('targetCompany', companyToUse);
            }
            
            const response = await axios.post('/api/upload-cam-switches', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            if (response.data.status) {
                setSuccess({ ...success, single: true });
                clearImportSwitchCamSupportError(true);
                
                // Clear the form after successful submission
                setTimeout(() => {
                    setSingleEntry({
                        company: availableCompanies[0] || '',
                        switch_name: '',
                        mac_address: '',
                        vlan_id: '',
                        interface: '',
                        entry_type: 'Dynamic',
                        age: '',
                        error: ''
                    });
                    setSuccess({ ...success, single: false });
                }, 3000);
            } else {
                const sourceMessage = response?.data?.msg || 'Failed to add CAM entry. Please try again.';
                setSingleEntry({ 
                    ...singleEntry, 
                    error: sourceMessage 
                });
                setImportSwitchCamSupportError(
                    FRONTEND_IMPORT_SWITCH_CAM_ERROR_CODES.SINGLE_ENTRY_FAILED,
                    'Import Switch CAM Page / Add Single Entry Failed',
                    sourceMessage
                );
            }
        } catch (error) {
            console.error('Error adding CAM entry:', error);
            const sourceMessage = error.response?.data?.msg || error?.message || 'Failed to add CAM entry. Please try again.';
            setSingleEntry({ 
                ...singleEntry, 
                error: sourceMessage 
            });
            setImportSwitchCamSupportError(
                FRONTEND_IMPORT_SWITCH_CAM_ERROR_CODES.SINGLE_ENTRY_FAILED,
                'Import Switch CAM Page / Add Single Entry Request Error',
                sourceMessage
            );
        } finally {
            setLoading(false);
        }
    };

    const handleImportFile = async (e) => {
        e.preventDefault();
        
        if (!camFile.value) {
            setCamFile({ ...camFile, error: 'Please select a file to upload' });
            setImportSwitchCamSupportError(
                FRONTEND_IMPORT_SWITCH_CAM_ERROR_CODES.VALIDATION_FAILED,
                'Import Switch CAM Page / File Import Validation Failed',
                'No file selected for CAM import'
            );
            return;
        }
        
        setLoading(true);
        
        try {
            const formData = new FormData();
            formData.append('camFile', camFile.value);
            
            if (targetCompany) {
                formData.append('targetCompany', targetCompany);
            }
            
            const response = await axios.post('/api/upload-cam-switches', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            if (response.data.status) {
                setSuccess({ ...success, file: true });
                setCamFile({ value: null, error: '' });
                clearImportSwitchCamSupportError(true);
            } else {
                const sourceMessage = response?.data?.msg || 'Failed to import CAM data. Please try again.';
                setCamFile({ 
                    ...camFile, 
                    error: sourceMessage 
                });
                setImportSwitchCamSupportError(
                    FRONTEND_IMPORT_SWITCH_CAM_ERROR_CODES.FILE_IMPORT_FAILED,
                    'Import Switch CAM Page / File Import Failed',
                    sourceMessage
                );
            }
        } catch (error) {
            console.error('Error importing CAM data:', error);
            const sourceMessage = error.response?.data?.msg || error?.message || 'Failed to import CAM data. Please try again.';
            setCamFile({ 
                ...camFile, 
                error: sourceMessage 
            });
            setImportSwitchCamSupportError(
                FRONTEND_IMPORT_SWITCH_CAM_ERROR_CODES.FILE_IMPORT_FAILED,
                'Import Switch CAM Page / File Import Request Error',
                sourceMessage
            );
        } finally {
            setLoading(false);
        }
    };

    if (error) {
        return (
            <Content headerTitle="Import Switch CAM Data" currentMenu="switchcam">
                <Error />
                {supportErrorMessage && (
                    <div className={styles.supportErrorCard}>
                        <strong>Support Error:</strong>
                        <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
                        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                            <button
                                type='button'
                                className={styles.copySupportErrorButton}
                                onClick={handleCopySupportError}
                            >
                                {copiedSupportError ? 'Copied' : 'Copy Error Details'}
                            </button>
                            <button
                                type='button'
                                className={styles.copySupportErrorButton}
                                onClick={() => clearImportSwitchCamSupportError(true)}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                )}
            </Content>
        );
    }

    return (
        <Content headerTitle="Import Switch CAM Data" currentMenu="switchcam">
            {loading && <Loader />}
            {supportErrorMessage && (
                <div className={styles.supportErrorBanner}>
                    <div className={styles.supportErrorBannerText}>
                        <strong>Support Error:</strong>
                        <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
                    </div>
                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                        <button
                            type='button'
                            className={styles.copySupportErrorButton}
                            onClick={handleCopySupportError}
                        >
                            {copiedSupportError ? 'Copied' : 'Copy Error Details'}
                        </button>
                        <button
                            type='button'
                            className={styles.copySupportErrorButton}
                            onClick={() => clearImportSwitchCamSupportError(true)}
                        >
                            Clear
                        </button>
                    </div>
                </div>
            )}
            
            <div className={styles.container}>
                <div className={styles.importOptionsContainer}>
                    {/* Single Entry Input Section */}
                    <div className={styles.importSection}>
                        <h3>Add Single CAM Entry</h3>
                        <p>Add a single CAM table entry to the database</p>
                        
                        <form onSubmit={handleAddSingleEntry} className={styles.singleRuleForm}>
                            <div className={styles.inputGroup}>
                                <div className={styles.formRow}>
                                    <div className={styles.formField}>
                                        <label>Company</label>
                                        <select 
                                            className={styles.select}
                                            value={singleEntry.company}
                                            onChange={(e) => setSingleEntry({ ...singleEntry, company: e.target.value, error: '' })}
                                        >
                                            {availableCompanies.map(company => (
                                                <option key={company} value={company}>
                                                    {company}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className={styles.formField}>
                                        <label>Switch Name *</label>
                                        <input 
                                            type="text"
                                            className={styles.formInput}
                                            placeholder="Enter switch name"
                                            value={singleEntry.switch_name}
                                            onChange={(e) => setSingleEntry({ ...singleEntry, switch_name: e.target.value, error: '' })}
                                        />
                                    </div>
                                </div>
                                
                                <div className={styles.formRow}>
                                    <div className={styles.formField}>
                                        <label>MAC Address *</label>
                                        <input 
                                            type="text"
                                            className={styles.formInput}
                                            placeholder="XX:XX:XX:XX:XX:XX"
                                            value={singleEntry.mac_address}
                                            onChange={(e) => setSingleEntry({ ...singleEntry, mac_address: e.target.value, error: '' })}
                                        />
                                    </div>
                                    <div className={styles.formField}>
                                        <label>VLAN ID *</label>
                                        <input 
                                            type="number"
                                            className={styles.formInput}
                                            placeholder="VLAN ID"
                                            value={singleEntry.vlan_id}
                                            onChange={(e) => setSingleEntry({ ...singleEntry, vlan_id: e.target.value, error: '' })}
                                        />
                                    </div>
                                </div>
                                
                                <div className={styles.formRow}>
                                    <div className={styles.formField}>
                                        <label>Interface *</label>
                                        <input 
                                            type="text"
                                            className={styles.formInput}
                                            placeholder="Interface name"
                                            value={singleEntry.interface}
                                            onChange={(e) => setSingleEntry({ ...singleEntry, interface: e.target.value, error: '' })}
                                        />
                                    </div>
                                    <div className={styles.formField}>
                                        <label>Entry Type</label>
                                        <select 
                                            className={styles.select}
                                            value={singleEntry.entry_type}
                                            onChange={(e) => setSingleEntry({ ...singleEntry, entry_type: e.target.value })}
                                        >
                                            <option value="Dynamic">Dynamic</option>
                                            <option value="Static">Static</option>
                                        </select>
                                    </div>
                                </div>
                                
                                <div className={styles.formRow}>
                                    <div className={styles.formField}>
                                        <label>Age (seconds)</label>
                                        <input 
                                            type="number"
                                            className={styles.formInput}
                                            placeholder="Age in seconds"
                                            value={singleEntry.age}
                                            onChange={(e) => setSingleEntry({ ...singleEntry, age: e.target.value, error: '' })}
                                        />
                                    </div>
                                    <div className={styles.formField}>
                                        {/* Empty field for layout balance */}
                                    </div>
                                </div>
                                
                                {singleEntry.error && <div className={styles.errorMessage}>{singleEntry.error}</div>}
                            </div>
                            
                            <button 
                                type="submit" 
                                className={styles.submitButton}
                                disabled={loading}
                            >
                                Add CAM Entry
                            </button>
                            
                            {success.single && (
                                <div className={styles.successMessage}>
                                    CAM entry added successfully!
                                </div>
                            )}
                        </form>
                    </div>
                    
                    {/* File Upload Section */}
                    <div className={styles.importSection}>
                        <h3>Import CAM Data from File</h3>
                        <p>Upload a CSV file containing multiple CAM entries</p>
                        
                        <button 
                            onClick={openFileModal} 
                            className={styles.fileUploadButton}
                            disabled={loading}
                        >
                            <div className={styles.buttonIcon}>
                                <svg className="navigation__icon">
                                    <use xlinkHref={`/assets/sprite.svg#icon-upload`} />
                                </svg>
                            </div>
                            <span>Upload File</span>
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Modal for importing from file */}
            <Modal show={showModal}>
                <ModalHeading heading="Import CAM Data from File" />
                
                {success.file ? (
                    <div className={styles.successMessage}>
                        CAM data imported successfully!
                        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                            <button 
                                onClick={closeModal}
                                className={styles.submitButton}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleImportFile}>
                        <div className={styles.modalContent}>
                            {/* Company selection for FLC users in modal - PUT THIS BACK */}
                            {userCompany === 'FLC' && availableCompanies.length > 0 && (
                                <div className={styles.modalInputGroup}>
                                    <label htmlFor="modalTargetCompany">Target Company:</label>
                                    <select 
                                        id="modalTargetCompany"
                                        value={targetCompany}
                                        onChange={(e) => setTargetCompany(e.target.value)}
                                        className={styles.select}
                                    >
                                        {availableCompanies.map(company => (
                                            <option key={company} value={company}>
                                                {company}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            
                            <div className={styles.modalInputGroup}>
                                <label>Select CAM Data File:</label>
                                <div className={styles.customFileInput}>
                                    <input 
                                        type="file"
                                        accept=".csv,.txt"
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files[0]) {
                                                setCamFile({ value: e.target.files[0], error: '' });
                                            }
                                        }}
                                    />
                                    <div className={styles.fileInputText}>
                                        <div className={styles.fileInputIcon}>📁</div>
                                        <span>
                                            {camFile.value ? camFile.value.name : 'Click to browse files'}
                                        </span>
                                        <small>CSV or TXT files only</small>
                                    </div>
                                </div>
                                {camFile.error && <div className={styles.errorMessage}>{camFile.error}</div>}
                                <p className={styles.fileHelp}>
                                    Required CSV format: Company,switch_name,mac_address,vlan_id,interface,entry_type,age<br/>
                                    
                                </p>
                            </div>
                        </div>
                        
                        <div className={styles.modalButtonsContainer}>
                            <button 
                                type="button" 
                                onClick={closeModal}
                                className={styles.cancelButton}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className={styles.importButton}
                                disabled={loading || !camFile.value}
                            >
                                {loading ? 'Importing...' : 'Import CAM Data'}
                            </button>
                        </div>
                    </form>
                )}
            </Modal>
        </Content>
    );
};

export default ImportSwitchCAM;
